<template>
  <component :is="type" :aria-label="ariaLabel" :class="['icon', 'icon--' + size, 'icon--' + name]">
    <span v-if="svg" v-html="svg" />
    <span v-if="label" :class="['icon__label']" :style="{ color: fill }">{{ label }}</span>
  </component>
</template>

<script>
import { Logger } from "@iloq/shared";
// const req = require.context(__dirname + "/../assets/icons/", true, /^\.\/.*\.svg$/);
const svgFiles = import.meta.glob("../assets/icons/*.svg", { eager: true });

/**
 * Icons are used to visually communicate core parts of the product and
 * available actions. They can act as wayfinding tools to help users more
 * easily understand where they are in the product.
 */
export default {
  name: "Icon",
  status: "prototype",
  release: "0.0.1",
  props: {
    /**
     * The name of the icon to display.
     */
    name: {
      type: String,
      required: true,
      default: "",
    },
    /**
     * The fill color of the SVG icon.
     */
    fill: {
      type: String,
      default: "#606060",
    },
    /**
     * Descriptive text to be read to screenreaders.
     */
    ariaLabel: {
      type: String,
      default: "icon",
    },
    /**
     * Count label(1-99) for showing ie. how many items are added to cart
     */
    count: {
      // eslint-disable-next-line vue/require-prop-type-constructor
      type: String | Number,
      default: "",
    },
    /**
     * Text label to be added after icon
     */
    label: {
      type: String,
      default: "",
    },
    /**
     * The html element name used for the icon.
     */
    type: {
      type: String,
      default: "span",
    },
    /**
     * The size of the icon. Defaults to medium.
     * `xxsmall, xsmall, small, medium, large`
     */
    size: {
      type: String,
      default: "small",
      validator: (value) => {
        return value.match(/(xxsmall|xsmall|small|medium|large)/);
      },
    },
  },
  data() {
    try {
      const svg = svgFiles("./" + this.name + ".svg")
        .replace(/fill="[^"]*"/, "")
        .replace(/style="[^"]*"/, "")
        .replace(/^<svg /, `<svg style="fill: ${this.fill}" `);

      return {
        svg: svg ?? null,
      };
    } catch (e) {
      Logger.log("No icon found", e);
      return {
        svg: null,
      };
    }
  },
};
</script>

<style lang="scss">
.icon {
  font-family: $font_text;
  @include reset;
  position: relative;
  margin-left: $space-xxs;
  margin-right: $space-xxs;
  display: inline-flex;
  justify-content: center;
  padding: 5px 5px 0 5px;
  svg {
    margin: 0;
    padding: 0;
  }
  &__label {
    font-size: $size-regular;
    vertical-align: middle;
    padding: 4px 0;
    margin: 0 $space-xs;
    display: none;
  }
  &--large svg {
    width: $space-l;
    height: $space-l;
  }
  &--medium svg {
    width: $space-m;
    height: $space-m;
  }
  &--small svg {
    width: 23px;
    height: 23px;
  }
  &--xsmall svg {
    width: 16px;
    height: 16px;
  }
  &--xxsmall svg {
    width: 12px;
    height: 12px;
  }
  &--xxxsmall svg {
    width: 8px;
    height: 8px;
  }
  @media (min-width-for("md")) {
    &__label {
      display: inline-block;
    }
  }
}
</style>
